import { datadogRum } from '@datadog/browser-rum';
import { type Auth, getAuth } from '@harbortouch/alexandria-utilities';
import type { LDClient } from 'launchdarkly-js-client-sdk';
import { type LocationData, getLocation } from './api/location';
import { DataOwnerType, getDataOwner } from './api/dataOwner';

const SHIFT4_EMAIL_DOMAINS = [
	'futurepos.com',
	'harbortouch.com',
	'rmpos.com',
	'unitedbankcard.com',
	'venuenext.com',
	'custcon.com',
	'shift4.com',
	'3dcart.com',
	'positouch.com',
	'microsnyc.com',
	'duetpos.com',
	'futurepos.com',
	'lighthousenetwork.com',
	'merchantlink.com',
	'venuenext.com',
	'custcon.com',
	'postec.com',
	'revelsystems.com',
];

export const initializeDatadog = (ldClient: LDClient, authenInfo: Auth['authenticated']) => {
	const trackUsageWithDataDog = ldClient.variation('lbm-track-usage-with-datadog');
	if (['staging', 'production'].includes(process.env.NODE_ENV!)) {
		datadogRum.init({
			applicationId: 'fc55d9cb-cebf-400c-806f-9c9412d4a8d7',
			clientToken: 'puba5d7dc3b663a9b89cab99d2b9affd2cc',
			site: 'datadoghq.com',
			service: 'lighthouse-frontend',
			env: process.env.NODE_ENV,
			version: '1.0.0',
			sessionSampleRate: 100,
			sessionReplaySampleRate: 1,
			trackViewsManually: false,
			trackUserInteractions: true,
			trackResources: true,
			trackLongTasks: true,
			defaultPrivacyLevel: 'mask-user-input',
			allowedTracingUrls: [(url) => url.startsWith(`${window.location.protocol}//${window.location.host}/api`)],
			beforeSend: () => trackUsageWithDataDog,
			startSessionReplayRecordingManually: false,
		});

		const isShift4Email =
			authenInfo?.email && SHIFT4_EMAIL_DOMAINS.some((domain) => authenInfo.email.toLowerCase().endsWith(`@${domain}`));
		datadogRum.setUser({
			id: authenInfo?.sub?.toString(),
			name: `${authenInfo.firstName} ${authenInfo.lastName}`,
			email: authenInfo.email,
			admin: !authenInfo.parentUserId,
			createdAt: authenInfo?.userSince ? new Date(authenInfo.userSince * 1000).toISOString() : undefined,
			internal: authenInfo.isSSO || isShift4Email,
		});
	}
};

export const setDataDogLocation = async (locationId: number | null, dataOwnerId?: number | null) => {
	const authInfo = getAuth();
	if (!authInfo || (locationId === null && dataOwnerId === null)) {
		return;
	}

	let locationData: LocationData;
	if (locationId === null) {
		const dataOwner = await getDataOwner(dataOwnerId!);
		if (!dataOwner || dataOwner.refType !== DataOwnerType.LOCATION) {
			return;
		}
		locationData = await getLocation(dataOwner.refId);
	} else {
		locationData = await getLocation(locationId);
	}

	datadogRum.setGlobalContextProperty('device', {
		locationId: locationId?.toString(),
		dataOwnerId: dataOwnerId?.toString(),
		locationName: locationData?.location.name,

		merchantId: locationData?.location.merchantId,
		shift4Serial: locationData?.location.shift4Serial,
		locationProduct: locationData?.location.product,
		locationCountry: locationData?.location.country,
	});
};

export const stopDataDogSession = () => {
	datadogRum.stopSession();
};
